import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApiService } from '../../services/adminApi';
import { usePageClamp } from '../../hooks/usePageClamp';
import { useSearchDraft } from '../../hooks/useSearchDraft';
import { urlEnum, urlPositiveInt, urlString, useUrlState } from '../../hooks/useUrlState';
import { SectionFilterBar, TableCard, TableHeader, TableViewport, usePageSize } from '../table/TableKit';
import {
  Badge,
  EmptyRow,
  ErrorNotice,
  LoadingSpinner,
  SortableTh,
  formatNumber,
  selectClass,
  tdClass,
  thClass,
} from './shared';
import RelativeTime from '../RelativeTime';
import { Tier } from '../../types';
import { TIERS, TIER_LABELS } from '../../utils/tiers';
import { fromHere } from '../../hooks/useBackTo';

const URL_SPEC = {
  tier: urlEnum(['', ...TIERS], ''),
  mode: urlEnum(['', 'temp', 'persistent'] as const, ''),
  active: urlEnum(['', 'true', 'false'] as const, ''),
  q: urlString(),
  sort: urlEnum(['name', 'instance_key', 'contact_email', 'instance_mode', 'tier', 'job_count', 'api_key_count', 'conversion_count', 'cli_operation_count', 'last_activity_at', 'created_at'] as const, 'created_at'),
  order: urlEnum(['asc', 'desc'] as const, 'desc'),
  page: urlPositiveInt(),
};

function AdminInstances() {
  const location = useLocation();
  const [pageSize, setPageSize] = usePageSize('admin-instances');
  const [state, setState] = useUrlState(URL_SPEC);
  const [searchInput, setSearchInput] = useSearchDraft(state.q, (q) => setState({ q, page: 1 }));
  const offset = (state.page - 1) * pageSize;

  const params = useMemo(
    () => ({
      limit: pageSize,
      offset,
      mode: state.mode || undefined,
      tier: state.tier || undefined,
      active: state.active || undefined,
      search: state.q || undefined,
      sort: state.sort,
      order: state.order,
    }),
    [pageSize, offset, state.tier, state.mode, state.active, state.q, state.sort, state.order],
  );

  const { data, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['admin', 'instances', params],
    queryFn: () => adminApiService.getInstances(params),
    placeholderData: (prev) => prev,
    retry: false,
  });

  usePageClamp({
    page: state.page,
    pageSize,
    total: data?.total,
    ready: !isLoading && !isPlaceholderData && !!data,
    setPage: (page) => setState({ page }),
  });

  const onSort = (sort: string, order: 'asc' | 'desc') => setState({
    sort: sort as typeof state.sort,
    order,
    page: 1,
  });

  return (
    <div>
      {error && <ErrorNotice message="Failed to load instances." />}
      <SectionFilterBar>
        <select
          aria-label="Mode filter"
          className={selectClass}
          value={state.mode}
          onChange={(e) => setState({ mode: e.target.value as typeof state.mode, page: 1 })}
        >
          <option value="">All Modes</option>
          <option value="temp">temp</option>
          <option value="persistent">persistent</option>
        </select>
        <select aria-label="Tier filter" className={selectClass} value={state.tier} onChange={(e) => setState({ tier: e.target.value as Tier | '', page: 1 })}>
          <option value="">All Tiers</option>
          {TIERS.map((tier) => <option key={tier} value={tier}>{TIER_LABELS[tier]}</option>)}
        </select>
        <select
          aria-label="Active filter"
          className={selectClass}
          value={state.active}
          onChange={(e) => setState({ active: e.target.value as typeof state.active, page: 1 })}
        >
          <option value="">All States</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </SectionFilterBar>
      <TableCard>
        <TableHeader
          title="Instances"
          search={{
            value: searchInput,
            onChange: setSearchInput,
            ariaLabel: 'Search instances',
          }}
          pagination={{
            offset,
            limit: pageSize,
            total: data?.total ?? 0,
            onOffsetChange: (nextOffset) => setState({ page: nextOffset / pageSize + 1 }),
            onLimitChange: (size) => {
              setPageSize(size);
              setState({ page: 1 });
            },
          }}
        />
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <TableViewport>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700">
                <SortableTh label="Name" sortKey="name" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Key" sortKey="instance_key" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Email" sortKey="contact_email" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Mode" sortKey="instance_mode" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <SortableTh label="Tier" sortKey="tier" sort={state.sort} order={state.order} onSort={onSort} firstOrder="asc" />
                <th className={thClass}>Credits</th>
                <th className={thClass}>Billing</th>
                <th className={thClass}>Referrals</th>
                <SortableTh label="Jobs" sortKey="job_count" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="API Keys" sortKey="api_key_count" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="API Conv." sortKey="conversion_count" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="CLI Ops" sortKey="cli_operation_count" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Last Activity" sortKey="last_activity_at" sort={state.sort} order={state.order} onSort={onSort} />
                <SortableTh label="Created" sortKey="created_at" sort={state.sort} order={state.order} onSort={onSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(data?.items ?? []).map((inst) => (
                <tr key={inst.id} className={inst.is_active ? '' : 'opacity-60'}>
                  <td className={`${tdClass} max-w-[220px] truncate`} title={inst.name}>{inst.name}</td>
                  <td className={`${tdClass} font-mono max-w-[220px] truncate`} title={inst.instance_key}>
                    {inst.instance_key}
                  </td>
                  <td className={`${tdClass} max-w-[260px] truncate`} title={inst.contact_email || undefined}>
                    {inst.contact_email || '—'}
                  </td>
                  <td className={tdClass}>
                    <span className="flex items-center gap-1">
                      <Badge value={inst.instance_mode} />
                      {!inst.is_active && <Badge value="revoked" />}
                    </span>
                  </td>
                  <td className={tdClass}>{inst.instance_mode === 'persistent' ? TIER_LABELS[inst.tier] : '—'}</td>
                  <td className={tdClass}>{inst.instance_mode === 'persistent' && inst.tier !== 'free' ? formatNumber(inst.credit_tokens) : '—'}</td>
                  <td className={tdClass}>
                    {inst.instance_mode === 'persistent' ? (
                      <Link to={`/admin/instances/${inst.id}/billing`} state={fromHere(location)} className="text-primary hover:text-primary-hover transition-colors">Billing</Link>
                    ) : '—'}
                  </td>
                  <td className={tdClass}>
                    {inst.referrals.length > 0
                      ? inst.referrals.map((r) => (
                          <span key={r} className="bg-gray-600/30 text-gray-300 px-2 py-0.5 rounded text-xs mr-1">
                            {r}
                          </span>
                        ))
                      : '—'}
                  </td>
                  <td className={tdClass}>{formatNumber(inst.job_count)}</td>
                  <td className={tdClass}>{formatNumber(inst.api_key_count)}</td>
                  <td className={tdClass}>{formatNumber(inst.conversion_count)}</td>
                  <td className={tdClass}>{formatNumber(inst.cli_operation_count)}</td>
                  <td className={tdClass}><RelativeTime value={inst.last_activity_at} /></td>
                  <td className={tdClass}><RelativeTime value={inst.created_at} /></td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && <EmptyRow colSpan={14} text="No instances found." />}
            </tbody>
          </table>
          </TableViewport>
        )}
      </TableCard>
    </div>
  );
}

export default AdminInstances;
